import { env } from '$lib/config/env';
import type { ScriptLayers, ScriptParams, ScriptSettings } from '$lib/core/script';
import { ApiClient, type SignIn } from './api-client';
import { safeStorage } from './storage';

export interface DocumentRecord {
  readonly id: string;
  readonly name: string;
  /** Goes up by one on every save, wherever it came from. */
  readonly rev: number;
  readonly createdAt: number;
  readonly updatedAt: number;
}

export interface StoredDocument extends DocumentRecord {
  /** The drawing's script. */
  readonly source: string;
  /** Its params, read by the script; drawings saved before params existed have none. */
  readonly params?: ScriptParams;
  /** Its layers, which the script puts shapes on; drawings saved before layers have none. */
  readonly layers?: ScriptLayers;
  /** Its settings, such as the view margin; drawings saved before settings have none. */
  readonly settings?: ScriptSettings;
}

/** What a save writes. */
export interface DocumentChange {
  readonly name: string;
  readonly source: string;
  readonly params: ScriptParams;
  readonly layers: ScriptLayers;
  /** Left out, the stored settings stay. */
  readonly settings?: ScriptSettings;
}

/** A save based on an older revision; carries what is stored now, such as Claude's edit. */
export class ConflictError extends Error {
  constructor(readonly current: StoredDocument) {
    super(`"${current.name}" changed while you were editing it`);
  }
}

export interface SaveOptions {
  /** The page is going away, so the browser should finish the save after it has gone. */
  readonly leaving?: boolean;
}

/**
 * Storage boundary for drawings, each stored as its script's text. Every save names the
 * revision it was based on, and a save based on an older one is refused, so the browser
 * and Claude never silently overwrite each other.
 */
export interface DocumentStore {
  list(): Promise<DocumentRecord[]>;
  load(id: string): Promise<StoredDocument | null>;
  create(
    name: string,
    source: string,
    params?: ScriptParams,
    layers?: ScriptLayers,
    settings?: ScriptSettings,
    options?: SaveOptions,
  ): Promise<StoredDocument>;
  save(
    id: string,
    change: DocumentChange,
    baseRev: number,
    options?: SaveOptions,
  ): Promise<StoredDocument>;
  remove(id: string): Promise<void>;
}

export type { SignIn };

/** Drawings on the server, through `/api/drawings`. */
export class RemoteDocumentStore implements DocumentStore {
  private readonly base = `${env.apiBaseUrl}/drawings`;
  private readonly api: ApiClient;

  constructor(storage: Storage | null = safeStorage(), signIn: SignIn | null = null) {
    this.api = new ApiClient(storage, signIn);
  }

  async list(): Promise<DocumentRecord[]> {
    return (await this.request('')) as DocumentRecord[];
  }

  async load(id: string): Promise<StoredDocument | null> {
    const response = await this.send(`/${encodeURIComponent(id)}`);
    if (response.status === 404) return null;
    return (await this.read(response)) as StoredDocument;
  }

  async create(
    name: string,
    source: string,
    params: ScriptParams = [],
    layers: ScriptLayers = [],
    settings?: ScriptSettings,
    options: SaveOptions = {},
  ): Promise<StoredDocument> {
    const body = { name, source, params, layers, settings };
    return (await this.read(await this.send('', 'POST', body, options.leaving))) as StoredDocument;
  }

  async save(
    id: string,
    change: DocumentChange,
    baseRev: number,
    options: SaveOptions = {},
  ): Promise<StoredDocument> {
    const body = { ...change, baseRev };
    const response = await this.send(`/${encodeURIComponent(id)}`, 'PUT', body, options.leaving);
    if (response.status === 409) {
      const { current } = (await response.json()) as { current: StoredDocument };
      throw new ConflictError(current);
    }
    return (await this.read(response)) as StoredDocument;
  }

  async remove(id: string): Promise<void> {
    await this.request(`/${encodeURIComponent(id)}`, 'DELETE');
  }

  private async request(path: string, method = 'GET', body?: unknown): Promise<unknown> {
    return this.read(await this.send(path, method, body));
  }

  private send(path: string, method = 'GET', body?: unknown, leaving = false): Promise<Response> {
    return this.api.send(this.base + path, method, body, leaving);
  }

  private read(response: Response): Promise<unknown> {
    return this.api.read(response);
  }
}

/** The same rules in memory, for tests. */
export class MemoryDocumentStore implements DocumentStore {
  private readonly drawings = new Map<string, StoredDocument>();
  private clock = 0;

  async list(): Promise<DocumentRecord[]> {
    return [...this.drawings.values()]
      .map(
        ({ source: _source, params: _params, layers: _layers, settings: _settings, ...record }) =>
          record,
      )
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async load(id: string): Promise<StoredDocument | null> {
    return this.drawings.get(id) ?? null;
  }

  async create(
    name: string,
    source: string,
    params: ScriptParams = [],
    layers: ScriptLayers = [],
    settings?: ScriptSettings,
  ): Promise<StoredDocument> {
    const now = (this.clock += 1);
    const drawing = {
      id: `drawing-${now}`,
      name,
      source,
      params,
      layers,
      ...(settings ? { settings } : {}),
      rev: 1,
      createdAt: now,
      updatedAt: now,
    };
    this.drawings.set(drawing.id, drawing);
    return drawing;
  }

  async save(id: string, change: DocumentChange, baseRev: number): Promise<StoredDocument> {
    const current = this.drawings.get(id);
    if (!current) throw new Error(`No drawing "${id}"`);
    if (current.rev !== baseRev) throw new ConflictError(current);
    const next = { ...current, ...change, rev: current.rev + 1, updatedAt: (this.clock += 1) };
    this.drawings.set(id, next);
    return next;
  }

  async remove(id: string): Promise<void> {
    this.drawings.delete(id);
  }
}
